/**
 * Lightweight sanitizer for free-text form inputs.
 *
 * Goal: strip any HTML tags and return a trimmed plain-text string so the
 * application does not send raw HTML or scripts to the API. This keeps
 * special characters intact while removing potential XSS payloads.
 *
 * We prefer a DOM-based approach when running in the browser/JSDOM so entities
 * are decoded and scripts/tags are removed. When `document` is not available
 * we fall back to a conservative regex-based tag stripper.
 */
export function sanitizeInput(input?: string | null): string | undefined {
  if (input === undefined || input === null) return undefined;

  const raw = String(input);

  if (typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = raw;

    const removeNodes = container.querySelectorAll('script, style');
    removeNodes.forEach(n => n.remove());
    const text = container.textContent || container.innerText || '';
    return text.trim();
  }

  // Fallback: loop tag-stripping until stable so nested/malformed tags
  // like "<<script>script>" cannot re-form after a single pass.
  // This path should only run in non-DOM environments.
  let result = raw;
  let previous = '';
  while (result !== previous) {
    previous = result;
    result = result.replaceAll(/<[^>]*>/g, '');
  }
  return result.trim();
}
